import Element from '../../../core/Element';
import { Color, Size, FontType, Align } from '../../../core/enums';
import type { SupportedHtmlTags, Theme, Tools } from '../../../core/types';
import { formatContent } from '../../../utilities/formatContent';
import { BackgroundImage } from '../../atoms/background-image';
import { Badge } from '../../archived/badge';
import { Box } from '../../archived/box';
import { Row, Column } from '../../atoms/grid';
import { Icon } from '../../atoms/icon';
import { useBreakpoint } from '../../styleless/breakpoint';
import { Carousel } from '../../styleless/carousel';
import styles from './index.module.less';

const { Container, Items, Item, Prev, Next, Dots } = Carousel;
interface Customization {
  headerHtmlTag?: keyof SupportedHtmlTags;
  headerTheme?: Theme;
  textHtmlTag?: keyof SupportedHtmlTags;
}
interface Props {
  data: {
    slides: {
      key?: string;
      imageUrl?: {
        desktop?: string;
        mobile?: string;
      };
      header: string | keyof React.ReactHTML | React.ReactNode;
      text: string | keyof React.ReactHTML | React.ReactNode;
      iconImage?: string;
      iconImageAltText?: string;
      disclaimer?: string | keyof React.ReactHTML | React.ReactNode;
      flagText?: string | keyof React.ReactHTML | React.ReactNode;
      imageTitle?: string;
    }[];
  };
  qeId?: string;
  customization?: Customization;
  withFlag?: boolean;
  tools?: Tools;
}

const defaultCustomization: Customization = {
  headerHtmlTag: 'h3',
  textHtmlTag: 'div',
  headerTheme: {},
};

const flagTheme: Theme = {
  borderColor: Color.DeepBlue,
  bgColor: Color.DeepBlue,
  rounded: Size.XXS,
  fontType: FontType.BentonMedium,
  marginLeft: 5,
  paddingLeft: 3,
  paddingRight: 3,
  paddingTop: 0,
  paddingBottom: 0,
};

export const ContentTileWithImageCarousel = ({
  data: { slides },
  qeId = 'ContentTileWithImageCarousel',
  customization = defaultCustomization,
  withFlag = false,
}: Props): React.ReactElement => {
  const { headerHtmlTag, textHtmlTag, headerTheme } = {
    ...defaultCustomization,
    ...customization,
  };
  const headerIds: string[] = slides.map((_, i) => `${qeId}-header-${i}`);

  const { sm } = useBreakpoint();
  return sm ? (
    <div data-qe-id={qeId}>
      {slides.map(
        (
          {
            imageUrl = {},
            key,
            header,
            text,
            disclaimer,
            iconImage,
            iconImageAltText = '',
            flagText,
            imageTitle,
          },
          i
        ) => (
          <Box
            $border={true}
            $glow="lighter"
            $overflow="hidden"
            theme={{
              rounded: Size.MD,
              marginTop: 10,
              marginBottom: 10,
            }}
            key={`ContentTileWithImageCarousel-${key ?? i}`}
          >
            <Element>
              {imageUrl.mobile && (
                <div className={styles.picture}>
                  <BackgroundImage
                    imageUrl={imageUrl.mobile}
                    height={120}
                    $fullWidth={true}
                    title={imageTitle ? imageTitle : undefined}
                  />
                </div>
              )}
              <Element
                id={headerIds[i]}
                Component={headerHtmlTag}
                theme={{
                  fontType: FontType.BentonMedium,
                  textHeading: 4,
                  paddingLeft: 15,
                  paddingTop: 0,
                  marginTop: 20,
                  marginBottom: 0,
                  verticallyFluid: true,
                  color: Color.DeepBlue,
                  ...headerTheme,
                }}
              >
                {formatContent(header)}
                {withFlag && flagText && (
                  <Badge
                    $type={'tag'}
                    theme={{
                      ...flagTheme,
                    }}
                  >
                    <span className={styles.flagMobileText}>{flagText}</span>
                  </Badge>
                )}
              </Element>
              {text && (
                <Element
                  Component={textHtmlTag}
                  theme={{
                    color: Color.Gray06,
                    textBody: 1,
                    fontType: FontType.HelveticaRegular,
                    padding: 20,
                  }}
                >
                  {text}
                </Element>
              )}
              {disclaimer && (
                <Element
                  theme={{
                    color: Color.Gray06,
                    fontType: FontType.HelveticaRegular,
                    paddingLeft: 20,
                    paddingRight: 20,
                    paddingBottom: 15,
                  }}
                  className={styles.disclaimer}
                >
                  {disclaimer}
                </Element>
              )}
              {iconImage && (
                <Element
                  Component="img"
                  src={iconImage}
                  alt={iconImageAltText}
                  className={styles.iconImage}
                  theme={{
                    paddingLeft: 30,
                    paddingBottom: 15,
                  }}
                />
              )}
            </Element>
          </Box>
        )
      )}
    </div>
  ) : (
    <div data-qe-id={qeId}>
      <Container totalItems={slides.length}>
        <Items>
          {slides.map(
            (
              {
                imageUrl = {},
                key,
                header,
                text,
                disclaimer,
                iconImage,
                iconImageAltText = '',
                flagText,
                imageTitle,
              },
              i
            ) => (
              <Item key={`ContentTileWithImageCarousel-${key ?? i}`} index={i}>
                <div className={styles.slideContainer}>
                  <div className={styles.boxContainer}>
                    <Box
                      $border={true}
                      $overflow="hidden"
                      theme={{
                        rounded: Size.MD,
                        verticallyFluid: true,
                      }}
                    >
                      <Element
                        theme={{
                          verticallyFluid: true,
                        }}
                      >
                        <Row
                          theme={{
                            verticallyFluid: true,
                          }}
                        >
                          {imageUrl && imageUrl.desktop && (
                            <Column $md="5" $mdPosition="last">
                              <Element
                                theme={{
                                  verticallyFluid: true,
                                }}
                              >
                                <BackgroundImage
                                  imageUrl={imageUrl.desktop}
                                  $fullWidth={true}
                                  title={imageTitle ? imageTitle : undefined}
                                />
                              </Element>
                            </Column>
                          )}
                          <Column
                            $md={imageUrl && imageUrl.desktop ? '7' : '12'}
                          >
                            <div
                              className={iconImage ? styles.slideWithIcon : ''}
                            >
                              <Element>
                                {header && (
                                  <Element
                                    id={headerIds[i]}
                                    Component={headerHtmlTag}
                                    theme={{
                                      fontType: FontType.BentonMedium,
                                      paddingLeft: 20,
                                      textHeading: 4,
                                      color: Color.DeepBlue,
                                      marginTop: 20,
                                      marginBottom: 0,
                                      ...headerTheme,
                                    }}
                                  >
                                    {formatContent(header)}
                                    {withFlag && flagText && (
                                      <Badge
                                        $type={'tag'}
                                        theme={{
                                          ...flagTheme,
                                        }}
                                      >
                                        <span
                                          className={styles.flagDesktopText}
                                        >
                                          {flagText}
                                        </span>
                                      </Badge>
                                    )}
                                  </Element>
                                )}
                                {text && (
                                  <Element
                                    Component={textHtmlTag}
                                    theme={{
                                      color: Color.Gray06,
                                      textBody: 1,
                                      fontType: FontType.HelveticaRegular,
                                      paddingTop: 20,
                                      paddingLeft: 20,
                                      paddingBottom: 15,
                                    }}
                                    className={styles.featureText}
                                  >
                                    {text}
                                  </Element>
                                )}
                              </Element>
                              <Element>
                                {iconImage && (
                                  <Element
                                    Component="img"
                                    src={iconImage}
                                    alt={iconImageAltText}
                                    aria-hidden={true}
                                    className={styles.iconImage}
                                    theme={{
                                      paddingLeft: 20,
                                      paddingBottom: 15,
                                      paddingTop: 15,
                                    }}
                                  />
                                )}
                              </Element>
                            </div>
                          </Column>
                        </Row>
                      </Element>
                    </Box>
                  </div>
                  {disclaimer && (
                    <Element
                      theme={{
                        color: Color.Gray06,
                        fontType: FontType.HelveticaRegular,
                        paddingLeft: 20,
                        paddingRight: 20,
                        paddingBottom: 15,
                        paddingTop: 15,
                      }}
                      className={styles.disclaimer}
                    >
                      {disclaimer}
                    </Element>
                  )}
                </div>
              </Item>
            )
          )}
        </Items>
        {slides.length > 1 ? (
          <Element theme={{ textAlign: Align.Center, marginTop: 20 }}>
            <Prev
              className={styles.navigation}
              tools={{
                tracking: {
                  methods: ['click'],
                  element: 'featuredbenefits',
                  details: ['carousel', 'left'],
                },
              }}
            >
              <Icon
                directory="us-consumer"
                imageName="axp-glyph-left"
                $size={Size.XS}
                theme={{ paddingRight: 5, color: Color.Gray05 }}
              />
            </Prev>
            <Dots
              headerIds={headerIds}
              clickAble={true}
              customization={{
                tracking: {
                  dot: {
                    tracking: {
                      methods: ['click'],
                      element: 'featuredbenefits',
                      details: ['carousel', 'dot'],
                    },
                  },
                },
              }}
              inactive={
                <Element
                  Component="span"
                  className={styles.inactiveDot}
                  theme={{
                    marginLeft: 5,
                    marginRight: 5,
                    bgColor: Color.Gray04,
                  }}
                />
              }
              active={
                <Element
                  Component="span"
                  className={styles.activeDot}
                  theme={{
                    marginLeft: 5,
                    marginRight: 5,
                    bgColor: Color.BrightBlue,
                  }}
                />
              }
            />
            <Next
              className={styles.navigation}
              tools={{
                tracking: {
                  methods: ['click'],
                  element: 'featuredbenefits',
                  details: ['carousel', 'right'],
                },
              }}
            >
              <Icon
                directory="us-consumer"
                imageName="axp-glyph-right"
                $size={Size.XS}
                theme={{ paddingLeft: 5, color: Color.Gray05 }}
              />
            </Next>
          </Element>
        ) : (
          <></>
        )}
      </Container>
    </div>
  );
};
